import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import {
  getRegistrationById,
} from "../../../services/adminApiService/coeRegistrationApi/CoeRegistrationApiService";
import { formatDateTime } from "../../../utils/dateTimeFormate";
import { downloadRegistrationPdfFile } from "./registrationFileUtils";

const displayValue = (val) => {
  if (val === null || val === undefined) return "NA";
  if (typeof val === "string" && val.trim() === "") return "NA";
  return val;
};

const RegistrationDetailsModal = ({
  show,
  onHide,
  registrationId,
}) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && registrationId) {
      fetchDetails(registrationId);
    } else {
      setDetails(null);
      setError(null);
    }
  }, [show, registrationId]);

  const fetchDetails = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getRegistrationById(id);
      const data = response?.data?.data || null;
      setDetails(data);
    } catch (err) {
      console.error("Error fetching registration details:", err);
      setError("Failed to load registration details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (details) {
      downloadRegistrationPdfFile(details);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Registration Details {details?.registration_no ? `(${details.registration_no})` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading applicant details...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger my-3">{error}</div>
        ) : details ? (
          <div className="container-fluid p-0">
            <div className="row g-3">
              <div className="col-12">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  Application Information
                </h6>
              </div>
              <div className="col-md-4">
                <strong>Registration No:</strong>
                <div>{displayValue(details.registration_no)}</div>
              </div>
              <div className="col-md-4">
                <strong>Discipline:</strong>
                <div>{displayValue(details.coe_sports_discipline)}</div>
              </div>
              <div className="col-md-4">
                <strong>Registration Date:</strong>
                <div>{displayValue(formatDateTime(details.created_at))}</div>
              </div>
              <div className="col-md-6">
                <strong>Centre Preference 1:</strong>
                <div>{displayValue(details.training_centre_preference_name_1)}</div>
              </div>
              <div className="col-md-6">
                <strong>Centre Preference 2:</strong>
                <div>{displayValue(details.training_centre_preference_name_2)}</div>
              </div>
              <div className="col-12 mt-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  Personal Information
                </h6>
              </div>
              <div className="col-md-4">
                <strong>Candidate Name:</strong>
                <div>{displayValue(details.candidate_name)}</div>
              </div>
              <div className="col-md-4">
                <strong>Gender:</strong>
                <div>{displayValue(details.gender)}</div>
              </div>
              <div className="col-md-4">
                <strong>Age:</strong>
                <div>{displayValue(details.age)}</div>
              </div>
              <div className="col-md-4">
                <strong>Date of Birth:</strong>
                <div>{displayValue(formatDateTime(details.dob))}</div>
              </div>
              <div className="col-md-4">
                <strong>Parent / Guardian Name:</strong>
                <div>{displayValue(details.parent_guardian_name)}</div>
              </div>
              <div className="col-md-4">
                <strong>Parent / Guardian Occupation:</strong>
                <div>{displayValue(details.parent_guardian_occupation)}</div>
              </div>
              <div className="col-md-4">
                <strong>School / University:</strong>
                <div>{displayValue(details.school_university)}</div>
              </div>
              <div className="col-md-4">
                <strong>Class / Subject:</strong>
                <div>{displayValue(details.class_subject)}</div>
              </div>

              <div className="col-12 mt-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  Contact Information
                </h6>
              </div>
              <div className="col-md-4">
                <strong>Mobile Number:</strong>
                <div>{displayValue(details.mobile_number)}</div>
              </div>
              <div className="col-md-4">
                <strong>Email:</strong>
                <div>{displayValue(details.email)}</div>
              </div>
              <div className="col-md-4">
                <strong>District:</strong>
                <div>{displayValue(details.district)}</div>
              </div>
              <div className="col-12">
                <strong>Present Address:</strong>
                <div>{displayValue(details.present_address)}</div>
              </div>
              <div className="col-12 mt-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  Sports Performance & Documents
                </h6>
              </div>
              <div className="col-md-4">
                <strong>Highest Performance:</strong>
                <div>{displayValue(details.highest_sports_performance)}</div>
              </div>
              <div className="col-md-4">
                <strong>Tournament Name:</strong>
                <div>{displayValue(details.tournament_name)}</div>
              </div>
              <div className="col-md-4">
                <strong>Certificate Type:</strong>
                <div>{displayValue(details.sports_certificate_type)}</div>
              </div>
              <div className="col-md-4">
                <strong>Age Proof Type:</strong>
                <div>{displayValue(details.age_proof_type)}</div>
              </div>
              <div className="col-md-4">
                <strong>Age Proof Document:</strong>
                <div>
                  {details.age_proof_document_url ? (
                    <a
                      href={details.age_proof_document_url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-primary btn-xs"
                    >
                      <i className="fa fa-id-card me-1"></i> View Age Proof
                    </a>
                  ) : (
                    "NA"
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <strong>Sports Certificate:</strong>
                <div>
                  {details.sports_certificate_document_url ? (
                    <a
                      href={details.sports_certificate_document_url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-primary btn-xs"
                    >
                      <i className="fa fa-file-text me-1"></i> View Certificate
                    </a>
                  ) : (
                    "NA"
                  )}
                </div>
              </div>

            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-muted">No details available.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleDownload}
          disabled={loading || !details}
        >
          <i className="fa fa-download me-1"></i> Download PDF
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RegistrationDetailsModal;
